/**
 * @file useChatSummary.ts
 * @description Число на кнопке чата и подсказка о событии
 * (chat-window-design.md §1а, §6 «Обновление»; SC64).
 *
 * `GET /admin/chat/summary` — раз в 15 с, в фоновой вкладке раз в 30 с.
 * Число — непрочитанные ДИАЛОГИ (у владельца и оператора — мои и без
 * ответственного, так считает сервер). Подсказка: последнее событие
 * (`latest`) сменилось — новое сообщение, передача мне, возврат из архива.
 * Первое чтение — точка отсчёта, без подсказки. Подсказки нет, пока окно
 * открыто. Для имени клиента и текста — карточка диалога; под сотрудником
 * (`readOnly`) карточку фоном не открываем — подсказка только из `summary`.
 * Опрос живёт в провайдере окна (`ChatWindowProvider`): число нужно и кнопке,
 * и «Диалог не выбран».
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { adminChat, type ChatSummary, type ConversationDetail } from "../../lib/api-chat";

export const SUMMARY_POLL_MS = 15_000;
export const SUMMARY_POLL_HIDDEN_MS = 30_000;

export interface ChatHint {
  kind: NonNullable<ChatSummary["latest"]>["kind"];
  conversationId: string;
  orderNumber: string | null;
  actorName: string | null;
  customerName: string | null;
  preview: string | null;
  /** Сколько событий пришло, пока подсказка на экране, кроме показанного. */
  more: number;
}

function latestKey(summary: ChatSummary | null): string | null {
  const l = summary?.latest;
  return l ? `${l.kind}:${l.conversationId}:${l.at}` : null;
}

export function useChatSummary(enabled: boolean, windowOpen: boolean, tick: number, readOnly = false) {
  const [summary, setSummary] = useState<ChatSummary | null>(null);
  const [hint, setHint] = useState<ChatHint | null>(null);
  const seenKey = useRef<string | null | undefined>(undefined);
  const openRef = useRef(windowOpen);
  openRef.current = windowOpen;
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;

  const showHint = useCallback(async (latest: NonNullable<ChatSummary["latest"]>) => {
    let card: ConversationDetail | null = null;
    // Под сотрудником карточку фоном не открываем (каждое открытие — запись в
    // журнале доступа к данным клиента): подсказка только из `summary`.
    if (!readOnlyRef.current) {
      try {
        card = await adminChat.findOne(latest.conversationId);
      } catch {
        card = null;
      }
    }
    if (openRef.current) return;
    setHint((prev) => ({
      kind: latest.kind,
      conversationId: latest.conversationId,
      orderNumber: latest.orderNumber,
      actorName: latest.actorName,
      customerName: card?.customer.displayName ?? card?.customer.email ?? null,
      preview: card?.lastMessage?.preview ?? null,
      more: prev ? prev.more + 1 : 0,
    }));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let timer = 0;
    const load = () => {
      adminChat
        .summary(controller.signal)
        .then((next) => {
          setSummary(next);
          const key = latestKey(next);
          const first = seenKey.current === undefined;
          if (!first && key && key !== seenKey.current && next.latest && !openRef.current) void showHint(next.latest);
          seenKey.current = key;
        })
        .catch(() => undefined)
        .finally(() => {
          if (controller.signal.aborted) return;
          const delay = document.visibilityState === "visible" ? SUMMARY_POLL_MS : SUMMARY_POLL_HIDDEN_MS;
          timer = window.setTimeout(load, delay);
        });
    };
    load();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [enabled, tick, showHint]);

  // Окно открыли — подсказка больше не нужна.
  useEffect(() => {
    if (windowOpen) setHint(null);
  }, [windowOpen]);

  const dismissHint = useCallback(() => setHint(null), []);

  return { unread: summary?.unreadConversations ?? 0, hint, dismissHint };
}
